








import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';
import Chart, { ChartConfiguration } from 'chart.js';
import _ from 'lodash';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { utilService } from '../util-service';
import chartUtil from './util.service';
import SwChartChartJsMixin from './sw-chart-chart-js.mixin.vue';
import { DoughnutroundedOptions, GaugeLollipopValue, GaugeLollipopLabelConfig } from './types';

interface AugmentedGaugeLollipopLabelConfig extends GaugeLollipopLabelConfig {
  theme: SwChartChartJsMixin['theme'];
}

/** Per the doc: https://chartjs-plugin-datalabels.netlify.com/guide/getting-started.html#registration
 * we have to unregister it first, then enable it locally for only this chart.
 */
Chart.plugins.unregister(ChartDataLabels);

@Component
export default class SwChartGaugeLollipop extends mixins(SwChartChartJsMixin) {
  @Prop({ type: Array, default () { return []; } })
  values: GaugeLollipopValue[];

  @Prop({ type: String, default: 'total' })
  limitMode: 'total' | 'separate';

  @Prop({ type: Array, required: true })
  labels: string[];

  @Prop({ type: Object, default() { return { enable: true }; } })
  pointerLabel: GaugeLollipopLabelConfig;

  @Prop({ type: Object, default() { return { enable: false }; } })
  onDataLabel: GaugeLollipopLabelConfig;

  @Prop({ type: Array, default() { return  utilService.colorConst.chartPrimaryPalette; } })
  palette: string[];

  @Prop(String)
  baseColor: string;

  @Prop({ type: Boolean, default: false })
  singleFullCircle: boolean;

  dataIndex = -1;
  datasetIndex = -1;

  get chartOptions(): Chart.ChartOptions {
    const options: Chart.ChartOptions = {
      cutoutPercentage: 25,
      legend: {
        display: false,
      },
      onHover: (evt, elems) => {// tslint:disable-line
        if (this.chartInstance) {
          const evtPoint = elems as any[];
          if (evtPoint && evtPoint.length > 0) {
            this.datasetIndex = evtPoint[0]._datasetIndex;
            this.dataIndex = evtPoint[0]._index;
          } else {
            this.dataIndex = -1;
            this.datasetIndex = -1;
          }
        }
      },
      plugins: {
        doughnutrounded: {
          applyToDatasets: this.values.map((value, index) => ({ index, dataIndices: [0] })),// tslint:disable-line
        } as DoughnutroundedOptions,
        gaugeLollipopPointerLabel: this.augmentedPointerLabelConfig.enable ? this.augmentedPointerLabelConfig : false,
        datalabels: {
          display: (context: any) => {
            if (!this.onDataLabel.enable) { return false; }
            if (context.dataIndex >= 1) { return false; }
            if (this.dataIndex === 0 && this.datasetIndex >= 0) {
              if (context.dataIndex !== this.dataIndex || context.datasetIndex !== this.datasetIndex) { return false; }
            }
            return true;
          },
          color: (context: any) => {
            return this.isActiveContext(context) ? utilService.colorConst.light_background : context.dataset.backgroundColor[0];
          },
          textShadowBlur: (context: any) => {
            return this.isActiveContext(context) ? 1 : 0;
          },
          textShadowColor: utilService.colorConst.light_text,
          backgroundColor: (context: any) => {
            return this.isActiveContext(context) ? context.dataset.backgroundColor[0] :
              chartUtil.hex2rgba(this.theme === 'light' ? utilService.colorConst.light_background : utilService.colorConst.dark_background, .7);
          },
          borderWidth: (context: any) => {
            return this.isActiveContext(context) ? 1 : 0;
          },
          borderColor: utilService.colorConst.light_background,
          borderRadius: 3,
          padding: { top: 3, right: 1, bottom: 0, left: 1 },
          font: { lineHeight: 1 },
          textAlign: 'center',
          formatter: (value: number, context: any) => {// tslint:disable-line
            const dataset: Chart.ChartDataSets = context.dataset;
            const formatter = this.onDataLabel.formatter || defaultDatasetOnDataLabelFormatter;
            return formatter(dataset.label, dataset.data as number[], this.isActiveContext(context));
          },
        },
      },
      tooltips: {
        enabled: false,
      },
    };
    return _.merge(options, this.options);
  }

  get normalizedDatasets() {
    const total = this.values.reduce((a, c) => (a + c.value), 0);
    const self = this;

    function getRestValue(value: GaugeLollipopValue) {
      if (self.limitMode === 'total') {
        if (!total) { return 100; }
        return total - value.value;
      }

      const limit = (value.limit || 0);
      if (value.value > limit) { return 0; }
      if (!value.value && !limit) { return 100; }
      return limit - value.value;
    }

    return this.values.map<Chart.ChartDataSets>((value, index) => {
      const datapoints = this.isInfiniteValue(value.value) ? [0, 100] :
        [value.value, getRestValue(value)];
      return {
        data: datapoints,
        label: this.labels[index % this.labels.length],
        backgroundColor: [this.palette[index % this.palette.length], this.singleFullCircle && index !== 0 ? 'transparent' : this.effectiveBaseColor],
        borderWidth: 2,
        borderColor: this.singleFullCircle && index !== 0 ?
          [chartUtil.elementBorderColor(this.theme), 'transparent'] :
          chartUtil.elementBorderColor(this.theme),
      };
    });
  }

  get effectiveBaseColor(): string {
    return this.baseColor || chartUtil.pointBackgroundColor(this.theme);
  }

  get augmentedPointerLabelConfig(): AugmentedGaugeLollipopLabelConfig {
    return Object.assign(
      {},
      this.pointerLabel,
      {
        theme: this.theme,
      },
    );
  }

  private isInfiniteValue(value: number) {
    return value === Number.POSITIVE_INFINITY || value === Number.NEGATIVE_INFINITY;
  }

  private isActiveContext(context: any) {
    if (context.active === true) { return true; }
    return context.datasetIndex === this.datasetIndex && context.dataIndex === this.dataIndex;
  }

  private updateData() {
    if (this.chartInstance) {
      chartUtil.updateChartRegularData(this.chartInstance, [], this.normalizedDatasets);
    }
  }

  @Watch('labels')
  @Watch('normalizedDatasets')
  @Watch('pointerLabel', { deep: true })
  @Watch('onDataLabel', { deep: true })
  @Watch('chartOptions')
  @Watch('dataIndex')
  @Watch('datasetIndex')
  private onConfigChanged() {
    if (this.chartInstance) {
      this.updateData();
      (this.chartInstance as any).options = this.chartOptions;
      this.chartInstance.update();
    }
  }

  private created() {
    this.myCssPrefix = 'sw-chart-gauge-lollipop';
  }

  private mounted() {
    this.initChart({
      type: 'doughnut',
      data: {
        labels: [],
        datasets: this.normalizedDatasets,
      },
      options: this.chartOptions,
      plugins: [
        pointerPlugin,
        ChartDataLabels,
      ],
    });
  }
}

/** for now this plugin is only used by this chart type, so we defined it locally */
const pointerPlugin = {
  id: 'gaugeLollipopPointerLabel',
  afterDatasetsDraw(chartInst: Chart, easing: string, opts?: AugmentedGaugeLollipopLabelConfig) {// tslint:disable-line
    if (!opts || !opts.enable) { return; }
    if (!chartInst.data.datasets || chartInst.data.datasets.length <= 0) { return; }

    chartInst.data.datasets.forEach((dataset, index) => {
      const arc = chartInst.getDatasetMeta(index).data[0];
      const arcInfo = {
        x: (chartInst.chartArea.left + chartInst.chartArea.right) / 2,
        y: (chartInst.chartArea.top + chartInst.chartArea.bottom) / 2,
        radius: (chartInst as any).outerRadius - (chartInst as any).radiusLength / 2 - (index * (chartInst as any).radiusLength),
        thickness: (chartInst as any).radiusLength / 2 - (typeof arc._model.borderWidth === 'number' ? arc._model.borderWidth : 2) / 2,
        backgroundColor: arc._model.backgroundColor,
      };
      const chartRadius: number = (chartInst as any).outerRadius;

      const ctx = chartInst.ctx!;
      const startAngle = Math.PI / 2 - (arc._view as any).startAngle;
      // const endAngle = Math.PI / 2 - (arc._view as any).endAngle;
      const pointRadius = Math.ceil(arcInfo.thickness * 0.4);
      const startX = arcInfo.radius * Math.sin(startAngle);
      const startY = arcInfo.radius * Math.cos(startAngle);
      const pointCenter = { x: startX - (arcInfo.thickness + pointRadius + 1), y: startY };
      const guideLineEndX = startX - (chartRadius - 2);

      ctx.save();
      ctx.translate(arcInfo.x, arcInfo.y);

      ctx.shadowOffsetY = 1;
      ctx.shadowColor = chartUtil.hex2rgba(chartUtil.chartTextColor(opts.theme), opts.theme === 'light' ? .6 : 1);
      // ctx.shadowBlur = 1;
      ctx.fillStyle = arcInfo.backgroundColor;
      ctx.font = Chart.helpers.fontString(
        Chart.defaults.global.defaultFontSize,
        Chart.defaults.global.defaultFontStyle,
        Chart.defaults.global.defaultFontFamily,
      );

      ctx.beginPath();
      ctx.moveTo(pointCenter.x, pointCenter.y);
      ctx.lineTo(guideLineEndX, pointCenter.y);
      ctx.lineWidth = 1;
      ctx.strokeStyle = arcInfo.backgroundColor;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'right';
      const formatter = opts.formatter || defaultDatasetPointerLabelFormatter;
      const label = formatter(dataset.label, dataset.data as number[]);
      ctx.fillText(label, guideLineEndX - 1, pointCenter.y + 1);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(
        pointCenter.x,
        pointCenter.y,
        pointRadius,
        0, 2 * Math.PI, true,
      );
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    });
  },
} as Exclude<ChartConfiguration['plugins'], undefined>[0];

function defaultDatasetPointerLabelFormatter(label: string | undefined, data: number[]) {
  if (!label || !data) { return 'N/A'; }
  const gaugeData = data;
  const total = gaugeData[0] + gaugeData[1];
  const percentage = _.round(total ? (gaugeData[0] * 100 / total) : 0, 1) ;
  return `${label}, ${percentage}%`;
}

function defaultDatasetOnDataLabelFormatter(label: string | undefined, data: number[], active: boolean) {
  if (!label || !data) { return 'N/A'; }
  const gaugeData = data;
  const total = gaugeData[0] + gaugeData[1];
  const percentage = _.round(total ? (gaugeData[0] * 100 / total) : 0, 1) ;
  return active ? `${label}\n${percentage}%` : `${percentage}%`;
}
